import {
  APP_INITIALIZER,
  ApplicationConfig,
  ErrorHandler,
  importProvidersFrom,
} from '@angular/core';
import * as Sentry from '@sentry/angular';
import { DatePipe, DOCUMENT } from '@angular/common';
import {
  HTTP_INTERCEPTORS,
  provideHttpClient,
  withFetch,
  withInterceptorsFromDi,
} from '@angular/common/http';
import { provideClientHydration } from '@angular/platform-browser';
import { Router, RouteReuseStrategy } from '@angular/router';
import { ToastrModule } from 'ngx-toastr';
import { provideAnimationsAsync } from '@angular/platform-browser/animations/async';
import { MAT_DIALOG_SCROLL_STRATEGY } from '@angular/material/dialog';

import { appRoutingProviders } from './app-routing.module';
import { CustomRouteReuseStrategy } from './custom-route-reuse.strategy';
import { IntegrationsService } from './shared/util/integrations';
import { AuthInterceptor } from './user/auth-interceptor';
import { ErrorInterceptor } from './shared/api/error.interceptor';
import { LoadingInterceptor } from './shared/api/loading.interceptor';
import { PageScrollLockStrategy } from './shared/services/page-scroll-lock';

/**
 * Analytics is configured during bootstrap but never awaited.
 *
 * The braces are load-bearing: this factory discards whatever init() returns,
 * so the initializer resolves immediately even if init() ever grows an
 * `await`. Angular delays the first render for as long as an initializer's
 * promise is pending, and third-party tags are not worth a visitor's paint.
 * See IntegrationsService.init(), which defers Hotjar and Sentry's replay
 * recorder to the first idle period. gtag is not among them and is not this
 * service's business at all: the inline gate in index.html installs its queue
 * during HTML parse - long before this bundle finishes downloading - and
 * fetches the tag itself once the browser goes idle.
 */
function initIntegrations(svc: IntegrationsService) {
  return () => {
    svc.init();
  };
}

/**
 * Providers shared by every bootstrap of this application.
 *
 * Kept apart from `main.ts` so a second entry point can reuse the exact same
 * list. `main.ts` keeps only what is true of the browser entry point alone:
 * prod mode and Sentry's own init, both of which are side effects rather than
 * providers.
 */
export const appConfig: ApplicationConfig = {
  providers: [
    IntegrationsService,
    appRoutingProviders,
    provideAnimationsAsync(),
    // Shared rather than browser-only on purpose: this is what tells the
    // *server* to annotate its output with `ngh` data, as well as what tells
    // the browser to reuse that DOM instead of throwing it away.
    //
    // It also turns on the HTTP transfer cache by default, which on a
    // prerendered route freezes any GET made during the render into the static
    // HTML at build time. Neither route prerendered today makes one - the
    // `ng-state` script holds `__nghData__` and nothing else - so the default
    // is left alone. Check that again before adding a route that fetches from
    // ngOnInit, and reach for withHttpTransferCacheOptions() if it does.
    provideClientHydration(),
    // withFetch is Angular's recommended backend under SSR (NG02801 warns
    // without it); platform-server's XHR fallback would also work. No call
    // site loses anything by it - nothing here asks for `reportProgress`,
    // which is the one HttpClient feature the fetch backend cannot do (upload
    // progress), and `responseType: 'blob'` is supported.
    provideHttpClient(withInterceptorsFromDi(), withFetch()),
    importProvidersFrom(ToastrModule.forRoot()),
    DatePipe,
    {
      provide: RouteReuseStrategy,
      useClass: CustomRouteReuseStrategy,
    },
    {
      provide: ErrorHandler,
      useValue: Sentry.createErrorHandler(),
    },
    {
      provide: Sentry.TraceService,
      deps: [Router],
    },
    {
      provide: APP_INITIALIZER,
      useFactory: () => () => {},
      deps: [Sentry.TraceService],
      multi: true,
    },
    {
      provide: APP_INITIALIZER,
      useFactory: initIntegrations,
      deps: [IntegrationsService],
      multi: true,
    },
    {
      // Every dialog freezes the page behind it with this instead of the CDK
      // default, which shifts the page (see PageScrollLockStrategy).
      provide: MAT_DIALOG_SCROLL_STRATEGY,
      useFactory: (document: Document) => () =>
        new PageScrollLockStrategy(document),
      deps: [DOCUMENT],
    },
    { provide: HTTP_INTERCEPTORS, useClass: AuthInterceptor, multi: true },
    { provide: HTTP_INTERCEPTORS, useClass: ErrorInterceptor, multi: true },
    { provide: HTTP_INTERCEPTORS, useClass: LoadingInterceptor, multi: true },
  ],
};
